import { Router, Request, Response } from 'express'
import { SAMLAuthService } from './service'
import { asyncHandler } from '../frameworks/error-handler'
import { ValidationError } from '../errors'

/**
 * Create SAML authentication routes
 */
export function createSAMLRoutes(samlAuthService: SAMLAuthService): Router {
  const router = Router()

  // ============ SAML Configuration ============

  /**
   * POST /saml/configure/:tenantId
   * Configure SAML for a tenant
   */
  router.post(
    '/saml/configure/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params
      const {
        spEntityID,
        acsUrl,
        sloUrl,
        idpEntityID,
        idpSsoUrl,
        idpSloUrl,
        idpCertificate,
        wantAssertionsSigned,
        wantResponseSigned,
        attributes,
        enabled
      } = req.body

      if (!tenantId || typeof tenantId !== 'string') {
        throw new ValidationError('Tenant ID must be a non-empty string')
      }

      if (!idpCertificate) {
        throw new ValidationError('IdP certificate is required')
      }

      const config = await samlAuthService.registerTenantSAMLConfig(
        tenantId,
        {
          sp: {
            entityID: spEntityID || `https://yourdomain.com/saml/${tenantId}`,
            acsUrl: acsUrl || `https://yourdomain.com/saml/acs/${tenantId}`,
            sloUrl: sloUrl || `https://yourdomain.com/saml/slo/${tenantId}`
          },
          idp: {
            entityID: idpEntityID,
            ssoUrl: idpSsoUrl,
            sloUrl: idpSloUrl,
            certificate: idpCertificate
          },
          attributes,
          wantAssertionsSigned: wantAssertionsSigned !== false,
          wantResponseSigned: wantResponseSigned !== false
        },
        enabled !== false
      )

      res.status(201).json({
        message: 'SAML configuration saved',
        config
      })
    })
  )

  /**
   * POST /saml/configure-from-metadata/:tenantId
   * Configure SAML from IdP metadata URL
   */
  router.post(
    '/saml/configure-from-metadata/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params
      const { metadataUrl } = req.body

      if (!metadataUrl || typeof metadataUrl !== 'string') {
        throw new ValidationError('Metadata URL must be a non-empty string')
      }

      const config = await samlAuthService.initializeFromMetadata(tenantId, metadataUrl)

      res.status(201).json({
        message: 'SAML configuration initialized from metadata',
        config
      })
    })
  )

  /**
   * GET /saml/metadata/:tenantId
   * Get SP metadata for federation
   */
  router.get(
    '/saml/metadata/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params

      const metadata = await samlAuthService.generateMetadata(tenantId)

      res.setHeader('Content-Type', 'application/xml')
      res.send(metadata)
    })
  )

  /**
   * DELETE /saml/configure/:tenantId
   * Disable SAML for a tenant
   */
  router.delete(
    '/saml/configure/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params

      await samlAuthService.disableSAML(tenantId)

      res.json({
        message: 'SAML disabled for tenant'
      })
    })
  )

  // ============ SAML Authentication ============

  /**
   * GET /saml/login/:tenantId
   * Initiate SAML login
   */
  router.get(
    '/saml/login/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params
      const { redirectUrl } = req.query

      const { redirectUrl: authUrl } = await samlAuthService.generateAuthRequest(tenantId, redirectUrl as string)

      res.redirect(authUrl)
    })
  )

  /**
   * POST /saml/acs/:tenantId
   * Assertion Consumer Service - Handle SAML response
   */
  router.post(
    '/saml/acs/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params
      const { SAMLResponse, RelayState } = req.body

      if (!SAMLResponse || typeof SAMLResponse !== 'string') {
        throw new ValidationError('SAML response is required')
      }

      const result = await samlAuthService.handleSAMLResponse(tenantId, SAMLResponse, RelayState)

      if (!result.success) {
        return res.status(401).json({
          error: 'SAML_AUTH_FAILED',
          message: result.error
        })
      }

      // Return authentication result
      res.json({
        userId: result.userId,
        email: result.email,
        session: result.session,
        profile: result.profile,
        isNewUser: result.isNewUser,
        redirectUrl: RelayState || '/'
      })
    })
  )

  /**
   * GET /saml/slo/:tenantId
   * Single Logout endpoint
   */
  router.get(
    '/saml/slo/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params

      // Clear session
      ;(req as any).kitiumSession = null

      res.json({
        message: 'Logged out successfully'
      })
    })
  )

  /**
   * POST /saml/logout/:tenantId
   * Initiate SAML logout
   */
  router.post(
    '/saml/logout/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params
      const { sessionIndex } = req.body

      const { redirectUrl } = await samlAuthService.generateLogoutRequest(tenantId, sessionIndex)

      res.json({
        redirectUrl
      })
    })
  )

  // ============ Health & Info ============

  /**
   * GET /saml/info/:tenantId
   * Get SAML configuration info for a tenant
   */
  router.get(
    '/saml/info/:tenantId',
    asyncHandler(async (req: Request, res: Response) => {
      const { tenantId } = req.params

      // Note: Don't return sensitive data like certificates
      const config = await samlAuthService['getTenantSAMLConfig'](tenantId)

      if (!config) {
        return res.status(404).json({
          error: 'SAML_NOT_CONFIGURED',
          message: 'SAML is not configured for this tenant'
        })
      }

      res.json({
        tenantId,
        configured: true,
        sp: {
          entityID: config.sp.entityID,
          acsUrl: config.sp.acsUrl,
          sloUrl: config.sp.sloUrl
        },
        idp: {
          entityID: config.idp.entityID,
          ssoUrl: config.idp.ssoUrl,
          sloUrl: config.idp.sloUrl
        },
        metadataUrl: `/saml/metadata/${tenantId}`
      })
    })
  )

  return router
}

/**
 * Express middleware to extract tenant ID from various sources
 */
export function extractTenantIdMiddleware(req: Request, res: Response, next: Function) {
  // Try to get tenant from URL parameter
  if (req.params.tenantId) {
    ;(req as any).tenantId = req.params.tenantId
    return next()
  }

  // Try to get tenant from header
  const tenantHeader = req.headers['x-tenant-id']
  if (tenantHeader && typeof tenantHeader === 'string') {
    ;(req as any).tenantId = tenantHeader
    return next()
  }

  // Try to get tenant from query parameter
  if (req.query.tenantId && typeof req.query.tenantId === 'string') {
    ;(req as any).tenantId = req.query.tenantId
    return next()
  }

  // Try to get tenant from authenticated user
  const apiKey = (req as any).apiKey
  if (apiKey?.orgId) {
    ;(req as any).tenantId = apiKey.orgId
    return next()
  }

  next()
}
